import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, Search } from "lucide-react";
import { http, errText } from "../lib/api";
import { Logo } from "../components/site/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const TOKEN = "mareia_admin_token";

const toCsv = (rows, cols) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + body], { type: "text/csv" }));
  a.download = "mareia-export.csv";
  a.click();
};

const Login = ({ onOk }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN, data.token);
      onOk(data);
    } catch (x) { setErr(errText(x)); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#073B4C] px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl bg-[#FBF7EF] p-8" data-testid="admin-login-form">
        <Logo />
        <h1 className="mt-6 font-display text-4xl">Admin</h1>
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="admin-email-input"
          className="mt-6 w-full rounded-xl border border-[#073B4C]/15 bg-white px-4 py-3 outline-none focus:border-[#0A7189]" />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mot de passe" data-testid="admin-password-input"
          className="mt-3 w-full rounded-xl border border-[#073B4C]/15 bg-white px-4 py-3 outline-none focus:border-[#0A7189]" />
        {err && <p className="mt-3 text-sm text-[#C2502F]" data-testid="admin-login-error">{err}</p>}
        <button className="mt-6 w-full rounded-full bg-[#073B4C] py-3 font-semibold text-[#FBF7EF] hover:bg-[#0A7189]" data-testid="admin-login-submit">Connexion</button>
        <Link to="/" className="mt-4 block text-center text-sm text-[#3F5F68] hover:underline">← Mareia</Link>
      </form>
    </div>
  );
};

const REG_COLS = ["created_at", "first_name", "last_name", "email", "phone", "address", "address2", "postal_code", "city", "country", "notes", "lang", "clicked_buy"];

const Dashboard = ({ onLogout }) => {
  const [regs, setRegs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    http.get("/admin/registrations").then((r) => setRegs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    http.get("/admin/newsletter").then((r) => setSubs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const filtered = useMemo(() => regs.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase())), [regs, q]);

  return (
    <div className="min-h-screen bg-[#FBF7EF]" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-[#073B4C]/10 px-5 py-4 md:px-10">
        <Link to="/"><Logo /></Link>
        <button onClick={onLogout} className="inline-flex items-center gap-2 text-sm" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" />Déconnexion</button>
      </header>
      <div className="mx-auto max-w-7xl px-5 py-10 md:px-10">
        <div className="grid grid-cols-3 gap-4">
          {[["Inscriptions", regs.length], ["Clics achat", regs.filter((r) => r.clicked_buy).length], ["Newsletter", subs.length]].map(([l, n]) => (
            <div key={l} className="rounded-2xl bg-white p-5"><p className="text-xs uppercase tracking-wider text-[#3F5F68]">{l}</p><p className="font-display text-4xl" data-testid={`admin-stat-${l}`}>{n}</p></div>
          ))}
        </div>
        <Tabs defaultValue="regs" className="mt-8">
          <TabsList><TabsTrigger value="regs" data-testid="admin-tab-registrations">Inscriptions</TabsTrigger><TabsTrigger value="subs" data-testid="admin-tab-newsletter">Newsletter</TabsTrigger></TabsList>
          <TabsContent value="regs">
            <div className="my-4 flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-1 items-center gap-2 rounded-xl border border-[#073B4C]/15 bg-white px-3"><Search className="h-4 w-4 text-[#3F5F68]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full bg-transparent py-2.5 outline-none" data-testid="admin-search-input" /></div>
              <button onClick={() => toCsv(filtered, REG_COLS)} className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#073B4C] px-4 py-2.5 text-sm text-[#FBF7EF]" data-testid="admin-export-csv"><Download className="h-4 w-4" />CSV</button>
            </div>
            <div className="overflow-x-auto rounded-2xl bg-white">
              <table className="w-full min-w-[900px] text-left text-sm" data-testid="admin-registrations-table">
                <thead className="bg-[#E4F2EF] text-xs uppercase tracking-wider text-[#3F5F68]">
                  <tr>{["Date", "Nom", "E-mail / Tél.", "Adresse de livraison", "Pays", "Achat"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-t border-[#073B4C]/10 align-top">
                      <td className="px-4 py-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString("fr-FR")}</td>
                      <td className="px-4 py-3">{r.first_name} {r.last_name}</td>
                      <td className="px-4 py-3">{r.email}<br /><span className="text-[#3F5F68]">{r.phone}</span></td>
                      <td className="px-4 py-3">{r.address}{r.address2 && <>, {r.address2}</>}<br />{r.postal_code} {r.city}{r.notes && <p className="text-[#3F5F68]">{r.notes}</p>}</td>
                      <td className="px-4 py-3">{r.country}</td>
                      <td className="px-4 py-3">{r.clicked_buy ? "✓" : "—"}</td>
                    </tr>
                  ))}
                  {!filtered.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#3F5F68]">Aucune inscription</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="subs">
            <button onClick={() => toCsv(subs, ["created_at", "email", "lang"])} className="my-4 inline-flex items-center gap-2 rounded-xl bg-[#073B4C] px-4 py-2.5 text-sm text-[#FBF7EF]" data-testid="admin-export-newsletter"><Download className="h-4 w-4" />CSV</button>
            <ul className="divide-y divide-[#073B4C]/10 rounded-2xl bg-white" data-testid="admin-newsletter-list">
              {subs.map((s) => <li key={s.id} className="flex justify-between px-4 py-3 text-sm"><span>{s.email}</span><span className="text-[#3F5F68]">{new Date(s.created_at).toLocaleDateString("fr-FR")}</span></li>)}
              {!subs.length && <li className="px-4 py-10 text-center text-sm text-[#3F5F68]">Aucun abonné</li>}
            </ul>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    localStorage.removeItem(TOKEN);
    setUser(false);
  };
  if (user === null) return <div className="min-h-screen bg-[#073B4C]" />;
  return user ? <Dashboard onLogout={logout} /> : <Login onOk={setUser} />;
}
